/**
 * Skeleton.tsx — layout-matching loading placeholders (E3 #4).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: E3 (premium polish).
 *
 * WHY: a bare centred `<Spinner>` gives no sense of what is loading and causes a
 * layout jump when the real content lands. A skeleton that mirrors the coming
 * layout (a card with a title bar and a few lines) reads as "almost there" and
 * keeps the page height stable. The gentle sheen is a single `gps-shimmer`
 * keyframe over a primary-light block; reduced-motion users get a STATIC block
 * (both via the JS gate here and the global animation kill in tokens.css).
 *
 * The tiny inline `<Spinner>` is kept for small in-place waits (a button's
 * "…"); skeletons replace the full-surface loaders (plan selection, dashboard,
 * profile, question flow). The STAFF console page-shaped compositions
 * (BoardSkeleton / TableSkeleton / DrawerSkeleton — UX Wave 6 §1) build on
 * this primitive from ui/skeletons.tsx; the patient journey's branded moment
 * lives in ui/BrandedLoader.tsx.
 */
import type { CSSProperties } from 'react';
import { usePrefersReducedMotion } from './motion';
import { Card } from './kit';

/** One shimmering block. Decorative — the enclosing region carries the busy label. */
export function Skeleton({
  width = '100%',
  height = '1rem',
  radius = 'var(--gps-r-sm)',
  style,
}: {
  width?: string;
  height?: string;
  radius?: string;
  style?: CSSProperties;
}) {
  const reduced = usePrefersReducedMotion();
  return (
    <span
      aria-hidden="true"
      style={{
        display: 'block',
        width,
        height,
        borderRadius: radius,
        // Reduced-motion → a flat primary-light block; otherwise a light band
        // sweeps across it via the `gps-shimmer` keyframe.
        background: reduced
          ? 'var(--gps-primary-light)'
          : 'linear-gradient(90deg, var(--gps-primary-light) 25%, var(--gps-surface-2) 37%, var(--gps-primary-light) 63%)',
        backgroundSize: '200% 100%',
        animation: reduced ? undefined : 'gps-shimmer 1.4s ease-in-out infinite',
        ...style,
      }}
    />
  );
}

/**
 * A Card-shaped skeleton: a title bar over `lines` text rows (the last one
 * short, as real copy tends to be). Announced to assistive tech as a busy
 * region so a screen reader hears the wait.
 */
export function SkeletonCard({ lines = 3, label = 'Loading…' }: { lines?: number; label?: string }) {
  return (
    <Card>
      <div role="status">
        <Skeleton width="45%" height="1.15rem" style={{ marginBottom: 'var(--gps-s-3)' }} />
        {Array.from({ length: lines }).map((_, i) => (
          // Static list (fixed length, never reordered) — the index is a stable key.
          <Skeleton key={i} height="0.9rem" width={i === lines - 1 ? '70%' : '100%'} style={{ marginBottom: 'var(--gps-s-2)' }} />
        ))}
        <span className="gps-sr-only">{label}</span>
      </div>
    </Card>
  );
}
